import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const BlueberryPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Blueberry Success: The Critical pH Factor"
        description="Research-backed pH management to maximize your blueberry profits. Learn how water quality affects your blueberry crop yield and quality."
        canonicalPath="/blueberry"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Blueberry Background.jpg"
            alt="Fresh blueberries with water droplets"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-indigo-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🫐</span>
              <span>High-Value Resource for Growers</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Blueberry Success:
              <span className="block text-blue-300">The Critical pH Factor</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management to maximize your profits
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-blue-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🫐</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-blue-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Blueberries are one of the most pH-sensitive crops you can grow</strong> — and your irrigation water could be quietly pushing your soil pH up season after season.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped orchard and berry growers tackle hidden irrigation problems for 40+ years. This guide explains why your water pH matters, what research says, and how a simple sulfur burner can help protect your soil, your yield, and your bottom line.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Why Blueberries Need Acidic Soil */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Why Blueberries Need Acidic Soil
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Blueberries thrive in strongly acidic soils, ideally between pH 4.5 and 5.5. When soil pH drifts above 5.5, iron, manganese, and other micronutrients become less available, leading to yellowing leaves, stunted growth, and poor berry quality. Even if your soil starts acidic, alkaline irrigation water can undo your work."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <a 
                  href="https://extension.uga.edu/publications/detail.html?number=B1291" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                >
                  Source: University of Georgia Extension — Soil & Fertilizer Management
                </a>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Key Takeaway:</span>
                </div>
                <p className="text-gray-700">Every irrigation you run affects your soil's long-term pH.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: OSU Research */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🎓</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: What OSU Found in Hermiston, Oregon
              </h2>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                A few years ago, researchers at Oregon State University established an organic northern highbush blueberry planting in Hermiston, Oregon on sandy, calcareous soil. They:
              </p>

              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Pre-acidified the soil with elemental sulfur to get closer to the blueberry pH sweet spot.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Irrigated through drip equipped with a sulfur burner and filtration to keep irrigation water in the right pH range instead of at the native high pH of the source.</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-green-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Monitored soil pH, nutrients, and soil biology while they tested compost, biochar, and other organic inputs on top of that acidified irrigation backbone.</span>
                </li>
              </ul>

              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Key Takeaway:</span>
                </div>
                <p className="text-gray-700">On challenging, high-pH sites, acidified irrigation is a structural decision. Soil health tools like compost and biochar work best once the water and pH are under control.</p>
              </div>

              <div className="text-sm text-gray-600 italic leading-relaxed">
                <p>This section is informed by organic blueberry research from Oregon State University in Hermiston, Oregon, including a{' '}
                  <a
                    href="https://www.nature.com/articles/s41598-025-05761-z"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 hover:underline"
                  >
                    soil health study in Scientific Reports
                  </a>, an{' '}
                  <a
                    href="https://eorganic.org/node/35679"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 hover:underline"
                  >
                    eOrganic webinar on soil health in blueberries
                  </a>, and{' '}
                  <a
                    href="https://horticulture.oregonstate.edu/article/optimizing-soil-ph-organic-blueberries-balancing-act"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-800 hover:underline"
                  >
                    OSU's article on optimizing soil pH for organic blueberries
                  </a>.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: The Hidden Risk - UPDATED */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: The Hidden Risk — High Bicarbonate Irrigation Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "buffers" floating in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily neutralize the acidity in your soil. It's like adding baking soda to your 
                blueberry beds, drop by drop, season after season. Even if your soil pH starts perfect, alkaline irrigation 
                water can gradually push it out of the ideal range.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "Irrigation water with high bicarbonate levels can gradually increase soil pH over time, making nutrients less available to acid-loving crops like blueberries."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <a 
                  href="https://pubs.extension.wsu.edu/product/acidifying-soil-for-crop-production-inland-pacific-northwest" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-orange-600 hover:text-orange-800 hover:underline transition-colors"
                >
                  Source: WSU Extension — Acidifying Soil for Crop Production in the Inland PNW
                </a>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Test your irrigation water's bicarbonate levels — not just soil pH. High-bicarbonate water can undo years of soil acidification work.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 4: Iron Chlorosis */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔬</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: Iron Chlorosis — How to Spot It
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-xl p-6 md:p-8 border border-yellow-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-yellow-500 pl-6">
                "Iron chlorosis is one of the most common nutrient problems in blueberries grown on soils or irrigated with water that has high pH or high bicarbonates. When root-zone pH rises above 5.5, iron becomes less soluble and plants show yellowing between leaf veins — classic iron deficiency."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-yellow-600" size={16} />
                <a 
                  href="https://catalog.extension.oregonstate.edu/em9215" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-yellow-600 hover:text-yellow-800 hover:underline transition-colors"
                >
                  Source: Oregon State University — Blueberry Nutrient Guide
                </a>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Grower Tip:</span>
                </div>
                <p className="text-gray-700">If you see pale leaves or weak growth, check your water pH and bicarbonates.</p>
              </div>

              {/* Visual Example */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4">
                  <img
                    src="/Blueberries.jpg"
                    alt="Healthy blueberry plants"
                    className="w-full h-32 md:h-48 object-cover rounded-lg mb-4"
                  />
                  <p className="text-sm text-gray-600 text-center">
                    <strong className="text-green-600">Healthy:</strong> Dark green leaves, vigorous growth
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 flex items-center justify-center">
                  <div className="text-center">
                    <AlertTriangle className="text-yellow-500 mx-auto mb-4" size={48} />
                    <p className="text-sm text-gray-600">
                      <strong className="text-yellow-600">Iron Chlorosis:</strong> Yellowing between leaf veins, stunted growth
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Drip Irrigation & Emitter Plugging */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Drip Irrigation & Emitter Plugging
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "In high-pH water, calcium and magnesium precipitate out as carbonates, which can clog drip emitters and reduce system uniformity. This means less consistent watering and fertilizer distribution — both of which directly affect berry size and quality."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <a 
                  href="https://edis.ifas.ufl.edu/publication/HS1352" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-purple-600 hover:text-purple-800 hover:underline transition-colors"
                >
                  Source: UF IFAS — Blueberry Irrigation Practices
                </a>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Why It Matters:</span>
                </div>
                <p className="text-gray-700">Sulfur burners help prevent emitter clogging by dissolving carbonates inline — so your system stays clean, efficient, and uniform.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-pH Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Poor fertilizer efficiency</li>
                    <li>• Reduced berry quality</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Better nutrient uptake</li>
                    <li>• Consistent berry size & quality</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 6: Water Sanity Check CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                6: Want a Sanity Check on Your Water?
              </h2>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 md:p-8 border border-teal-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                If you are growing blueberries in a high-pH environment, a quick look at your water and soil reports usually tells us a lot about whether acidified irrigation is worth modeling.
              </p>

              <h4 className="text-xl font-semibold text-gray-900 mb-4">Here is what to send us:</h4>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Your most recent soil pH and basic soil test for each block</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Your irrigation water pH</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Your irrigation water alkalinity or bicarbonates (ppm as CaCO3)</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">A short note about your acreage and irrigation setup (drip, solid set, etc.)</span>
                </li>
              </ul>

              <h4 className="text-xl font-semibold text-gray-900 mb-4">We will review your reports and give you a simple written recommendation:</h4>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">Whether acidified irrigation is likely to pay off for your blocks</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">How a sulfur burner could be configured for your flows</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle className="text-teal-600 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">How it could fit alongside any compost or soil health program you are already running</span>
                </li>
              </ul>

              <div className="flex flex-col sm:flex-row gap-4">
                <motion.button
                  onClick={() => setIsQuoteModalOpen(true)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-teal-600 text-white px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors inline-flex items-center justify-center space-x-2 shadow-lg"
                >
                  <span>Get Your Free Water Analysis</span>
                  <ArrowRight size={20} />
                </motion.button>

                <motion.a
                  href="/sulfur-burners"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="border-2 border-teal-600 text-teal-600 px-6 md:px-8 py-3 md:py-4 rounded-lg font-semibold hover:bg-teal-600 hover:text-white transition-colors inline-flex items-center justify-center"
                >
                  Explore Sulfur Burner Options
                </motion.a>
              </div>
            </div>
          </motion.div>

          {/* Section 7: Sulfur Burners Solution - UPDATED */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-ctc-blue to-ctc-blue-dark rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔄</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                7: Sulfur Burners — A Safer Solution
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-ctc-blue/5 to-ctc-blue/10 rounded-xl p-6 md:p-8 border border-ctc-blue/20">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-ctc-blue pl-6">
                "Inline sulfur burners generate sulfurous acid from elemental sulfur, providing a safe, controlled method to reduce irrigation water pH, neutralize bicarbonates, and deliver sulfate sulfur to the crop. Growers appreciate not having to handle bulk sulfuric acid — and the system runs automatically inline."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-ctc-blue" size={16} />
                <span className="text-sm font-medium text-ctc-blue">
                  Source: UC ANR & grower trials
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">Bonus:</span>
                </div>
                <p className="text-gray-700">CTC Waterworks now offers easy automation upgrades for hands-free operation</p>
              </div>

              {/* CTC Advantages */}
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Blueberries:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging and system damage',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in berry operations'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Blueberries" />

      {/* Resources Section */}
      <section className="py-6 bg-gray-50 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-sm text-gray-600 italic text-center">
            Additional resources: Organic blueberry research from Oregon State University in Hermiston, Oregon (
            <a
              href="https://www.nature.com/articles/s41598-025-05761-z"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ctc-blue hover:text-ctc-blue-dark hover:underline"
            >
              Scientific Reports
            </a>, {' '}
            <a
              href="https://catalog.extension.oregonstate.edu/em9215"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ctc-blue hover:text-ctc-blue-dark hover:underline"
            >
              OSU Extension
            </a>, and {' '}
            <a
              href="https://eorganic.org/node/35679"
              target="_blank"
              rel="noopener noreferrer"
              className="text-ctc-blue hover:text-ctc-blue-dark hover:underline"
            >
              eOrganic
            </a>).
          </p>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BlueberryPage;